// Design group / draft token helpers.
//
// The `rk-<base36 time>-<base36 rand>` token is the one identity a design
// carries through its whole life: minted at the FIRST spot save, stored in
// the PersistedState envelope, used as the BuilderDraft id for server-side
// abandonment recovery, and finally emitted on the cart line as
// `_rk_group_id` at add-to-cart (where BuilderDesign + the orders/create
// webhook pick it up). Keeping mint + validation here (pure, no DOM) means
// App.tsx, the worker routes, and the tests can never drift on the format.

/** Matches the token shape the admin endpoints validate (`^rk-[a-z0-9-]+$`). */
export const GROUP_ID_PATTERN = /^rk-[a-z0-9-]+$/i;

/** Max length accepted by the admin's save-design / save-draft routes. */
export const GROUP_ID_MAX_LENGTH = 64;

/** Mint a fresh token. Format is load-bearing: `rk-<base36>-<base36>`,
 *  identical to the historical inline mint in App.tsx onAddToCart. */
export function mintGroupId(): string {
  return `rk-${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 7)}`;
}

/** True when `v` is a string in the accepted token shape. */
export function isValidGroupId(v: unknown): v is string {
  return (
    typeof v === "string" &&
    v.length > 3 &&
    v.length <= GROUP_ID_MAX_LENGTH &&
    GROUP_ID_PATTERN.test(v)
  );
}

/** Reuse a previously-minted token when it's valid; mint otherwise.
 *  This is the add-to-cart fallback: a design that went through a spot
 *  save already has its token, anything else (corrupt storage, legacy
 *  persisted state from before tokens moved to spot save) mints fresh,
 *  exactly as add-to-cart always did. */
export function reuseOrMintGroupId(existing: string | null | undefined): string {
  return isValidGroupId(existing) ? existing : mintGroupId();
}
